/**
 * Modbus RTU Decoder for hex string responses
 * Decodes Modbus function code 0x03 (Read Holding Registers) responses
 */

import {
  bytesToUint16,
  bytesToInt16,
  parseChargeDischargeStatus,
  parseSystemStatus,
  parseSelfTestStatus,
  parseContactorStatus,
  parseAlarmEvents,
  parsePrimaryProtectionEvents,
  parseSecondaryProtectionEvents
} from './rs485Parser';
import { BATTERY_TYPE, slaveIdToHexPrefix } from './assetConfig';
import { getDalyRegisterInfo } from './dalyRegisters';

// Convert hex string to byte array
export const hexStringToBytes = (hexString) => {
  if (!hexString || typeof hexString !== 'string') return [];
  const hex = hexString.replace(/\s/g, '');
  const bytes = [];
  for (let i = 0; i < hex.length; i += 2) {
    bytes.push(parseInt(hex.substr(i, 2), 16));
  }
  return bytes;
};

/**
 * Normalize wrapped Modbus request keys (e.g. from AIS trackers) to bare hex string
 * @param {string} key - Modbus request key (bare hex or AIS wrapped command)
 * @returns {string|null} Uppercase hex string >= 12 chars, or null
 */
export const normalizeRequestHex = (key) => {
  if (!key || typeof key !== 'string') return null;
  let s = key.trim();
  if (s.includes(':')) {
    const idx = s.lastIndexOf(':');
    s = s.substring(idx + 1);
  }
  s = s.replace(/\*+$/, '').trim();
  if (/^[0-9a-fA-F]+$/.test(s) && s.length >= 12) {
    return s.toUpperCase();
  }
  return null;
};

/**
 * Strip prepended request echo from historic Modbus responses
 * @param {string} requestHex - Normalized request hex
 * @param {string} responseHex - Response hex string
 * @returns {string} Response hex with request prefix stripped if present
 */
export const stripRequestEcho = (requestHex, responseHex) => {
  if (!responseHex || typeof responseHex !== 'string') return responseHex;
  const cleanResponse = responseHex.replace(/\s/g, '');
  if (!requestHex || typeof requestHex !== 'string') return cleanResponse;
  const cleanRequest = requestHex.replace(/\s/g, '').toUpperCase();
  if (cleanResponse.toUpperCase().startsWith(cleanRequest)) {
    return cleanResponse.slice(cleanRequest.length);
  }
  return cleanResponse;
};

// Decode Modbus RTU response for function code 0x03
// Supports both standard Modbus RTU format and raw register data format
export const decodeModbusResponse = (hexResponse, expectedSlaveId = null, expectedNumRegisters = null, type = BATTERY_TYPE.GCE) => {
  if (!hexResponse) return null;

  const bytes = hexStringToBytes(hexResponse);
  if (bytes.length < 2) return null;

  const slaveAddress = bytes[0];
  const functionCode = bytes[1];

  // Check if this is a standard Modbus RTU response (FC=0x03)
  if (functionCode === 0x03 && bytes.length >= 3) {
    const declared = bytes[2];
    const available = bytes.length - 3;
    const usable = Math.min(declared, available);
    if (usable >= 2) {
      const registerBytes = bytes.slice(3, 3 + usable);
      const registers = [];
      for (let i = 0; i < registerBytes.length; i += 2) {
        if (i + 1 < registerBytes.length) {
          registers.push(bytesToUint16(registerBytes[i], registerBytes[i + 1]));
        }
      }
      return {
        slaveAddress,
        functionCode,
        byteCount: usable,
        registers,
        rawBytes: bytes,
        isRawData: false,
        truncated: available < declared
      };
    }
  }

  // Guard the raw fallback for DALY. The raw branch exists for devices that return headerless register data;
  // for DALYLV it turns line noise into plausible-looking registers. When type === BATTERY_TYPE.DALYLV and the
  // RTU branch did not match, return null instead of raw-decoding.
  if (type === BATTERY_TYPE.DALYLV) {
    return null;
  }

  // If not standard format, treat the entire response as raw register data
  // This handles cases where the API returns data without Modbus header
  // Strip the last 2 bytes if they appear to be CRC (response length is longer than expected)
  let dataBytes = bytes;
  if (expectedNumRegisters && bytes.length > expectedNumRegisters * 2) {
    // Likely has CRC at the end, strip it
    dataBytes = bytes.slice(0, -2);
  }

  const registers = [];
  for (let i = 0; i < dataBytes.length; i += 2) {
    if (i + 1 < dataBytes.length) {
      registers.push(bytesToUint16(dataBytes[i], dataBytes[i + 1]));
    }
  }

  return {
    slaveAddress: expectedSlaveId || 0,
    functionCode: 0x03,
    byteCount: dataBytes.length,
    registers,
    rawBytes: bytes,
    isRawData: true,
    truncated: false
  };
};

// Parse register address from request hex string
export const parseModbusRequest = (hexRequest) => {
  if (!hexRequest) return null;

  const bytes = hexStringToBytes(hexRequest);
  if (bytes.length < 6) return null;

  const slaveAddress = bytes[0];
  const functionCode = bytes[1];
  const startAddress = bytesToUint16(bytes[2], bytes[3]);
  const numRegisters = bytesToUint16(bytes[4], bytes[5]);

  return {
    slaveAddress,
    functionCode,
    startAddress,
    numRegisters,
    startAddressHex: '0x' + startAddress.toString(16).toUpperCase().padStart(4, '0')
  };
};

// Map register addresses to parameter names based on GCE or DALY protocol
export const getRegisterInfo = (address, type = BATTERY_TYPE.GCE) => {
  if (type === BATTERY_TYPE.DALYLV) {
    return getDalyRegisterInfo(address);
  }

  const registerMap = {
    // SBMS Running Status (0x1000-0x1028)
    0x1000: { name: 'Total Voltage', unit: 'V', multiplier: 0.1, signed: false },
    0x1001: { name: 'Charge/Discharge Status', unit: '', parser: parseChargeDischargeStatus },
    0x1002: { name: 'Current/Positive Half Cluster Current', unit: 'A', multiplier: 0.1, signed: true },
    0x1003: { name: 'Negative Half Cluster Current', unit: 'A', multiplier: 0.1, signed: true },
    0x1004: { name: 'Positive Half Cluster Voltage', unit: 'V', multiplier: 0.1, signed: false },
    0x1005: { name: 'Negative Half Cluster Voltage', unit: 'V', multiplier: 0.1, signed: false },
    0x1006: { name: 'Battery Pack SOC', unit: '%', multiplier: 1, signed: false },
    0x1007: { name: 'Battery Pack SOH', unit: '%', multiplier: 1, signed: false },
    0x1008: { name: 'Alarm Events', unit: '', parser: parseAlarmEvents },
    0x1009: { name: 'Primary Protection Events', unit: '', parser: parsePrimaryProtectionEvents },
    0x100A: { name: 'Secondary Protection Events', unit: '', parser: parseSecondaryProtectionEvents },
    0x100B: { name: 'Current Available Charging Capacity', unit: 'kWh', multiplier: 1, signed: false },
    0x100C: { name: 'Current Available Discharge Capacity', unit: 'kWh', multiplier: 1, signed: false },
    0x100D: { name: 'Current Maximum Allowable Charging Power', unit: 'kW', multiplier: 1, signed: false },
    0x100E: { name: 'Current Maximum Allowable Discharge Power', unit: 'kW', multiplier: 1, signed: false },
    0x100F: { name: 'System Status', unit: '', parser: parseSystemStatus },
    0x1010: { name: 'RBMS Communication Status', unit: '', multiplier: 1, signed: false },
    0x1011: { name: 'Charging Enable', unit: '', multiplier: 1, signed: false },
    0x1012: { name: 'Discharging Enable', unit: '', multiplier: 1, signed: false },
    0x1013: { name: 'Number of Online Cabinets', unit: '', multiplier: 1, signed: false },
    0x1014: { name: 'System Highest Cell Voltage', unit: 'mV', multiplier: 1, signed: false },
    0x1018: { name: 'System Minimum Cell Voltage', unit: 'mV', multiplier: 1, signed: false },
    0x101C: { name: 'System Maximum Temperature', unit: '°C', multiplier: 1, signed: true },
    0x1020: { name: 'System Minimum Temperature', unit: '°C', multiplier: 1, signed: true },
    0x1024: { name: 'Maximum Cabinet Voltage in Online Cabinet', unit: 'mV', multiplier: 1, signed: false },
    0x1028: { name: 'Remaining Backup Time', unit: 'mins', multiplier: 1, signed: false },

    // RBMS Single Battery Cabinet (0x1100-0x1123)
    0x1100: { name: 'Total Voltage', unit: 'V', multiplier: 0.1, signed: false },
    0x1101: { name: 'Charge/Discharge Status', unit: '', parser: parseChargeDischargeStatus },
    0x1102: { name: 'BMS Self-test Status', unit: '', parser: parseSelfTestStatus },
    0x1105: { name: 'Circuit Breaker/Contactor Status', unit: '', parser: parseContactorStatus },
    0x1106: { name: 'Current', unit: 'A', multiplier: 0.1, signed: true },
    0x1107: { name: 'SOC', unit: '%', multiplier: 1, signed: false },
    0x1108: { name: 'SOH', unit: '%', multiplier: 1, signed: false },
    0x1109: { name: 'Maximum Temperature', unit: '°C', multiplier: 1, signed: true },
    0x110C: { name: 'Minimum Temperature', unit: '°C', multiplier: 1, signed: true },
    0x110F: { name: 'Maximum Cell Voltage', unit: 'mV', multiplier: 1, signed: false },
    0x1112: { name: 'Minimum Cell Voltage', unit: 'mV', multiplier: 1, signed: false },
    0x1115: { name: 'Alarm Events', unit: '', parser: parseAlarmEvents },
    0x1116: { name: 'Primary Protection Events', unit: '', parser: parsePrimaryProtectionEvents },
    0x111E: { name: 'Secondary Protection Events', unit: '', parser: parseSecondaryProtectionEvents },
    0x1120: { name: 'Charging Enable Control', unit: '', multiplier: 1, signed: false },
    0x1121: { name: 'Discharging Enable Control', unit: '', multiplier: 1, signed: false },
    0x1122: { name: 'Current Max Charge Power', unit: 'kW', multiplier: 1, signed: false },
    0x1123: { name: 'Current Max Discharge Power', unit: 'kW', multiplier: 1, signed: false },

    // Battery Data Registers (0x2000-0x23FF)
    // Cell voltages and temperatures - handled dynamically
  };

  // Handle battery data registers (0x2000-0x23FF)
  if (address >= 0x2000 && address <= 0x23FF) {
    const offset = address - 0x2000;
    const moduleNum = Math.floor(offset / 32) + 1;
    const cellOffset = offset % 32;

    if (cellOffset < 24) {
      return {
        name: `Module ${moduleNum} Cell ${cellOffset + 1} Voltage`,
        unit: 'mV',
        multiplier: 1,
        signed: false
      };
    } else {
      return {
        name: `Module ${moduleNum} Temperature ${cellOffset - 23}`,
        unit: '°C',
        multiplier: 1,
        signed: true
      };
    }
  }

  return registerMap[address] || {
    name: `Register 0x${address.toString(16).toUpperCase()}`,
    unit: '',
    multiplier: 1,
    signed: false
  };
};

// Parse complete modbus data object (key-value pairs of request-response)
export const parseModbusData = (modbusDataObject, slaveId = null, type = BATTERY_TYPE.GCE) => {
  if (!modbusDataObject || typeof modbusDataObject !== 'object') {
    return [];
  }

  const parsedData = [];

  Object.entries(modbusDataObject).forEach(([rawRequestKey, rawResponseHex]) => {
    const normalizedReq = normalizeRequestHex(rawRequestKey);
    if (!normalizedReq) {
      return;
    }

    // Filter by slave ID if specified
    if (slaveId !== null) {
      const prefix = slaveIdToHexPrefix(slaveId);
      if (!normalizedReq.toUpperCase().startsWith(prefix)) {
        return;
      }
    }

    const request = parseModbusRequest(normalizedReq);
    if (!request) {
      console.log('Failed to parse request:', rawRequestKey);
      return;
    }

    const cleanResponseHex = stripRequestEcho(normalizedReq, rawResponseHex);

    // Pass expected slave ID and register count to help decode raw data format, plus battery type
    const response = decodeModbusResponse(cleanResponseHex, request.slaveAddress, request.numRegisters, type);

    if (!response) {
      return;
    }

    // If expected registers don't match, try to limit to expected count
    if (request.numRegisters && response.registers.length > request.numRegisters) {
      response.registers = response.registers.slice(0, request.numRegisters);
    }

    // Parse each register in the response
    response.registers.forEach((registerValue, index) => {
      const registerAddress = request.startAddress + index;
      const registerInfo = getRegisterInfo(registerAddress, type);

      let parsedValue;
      if (registerInfo.parser) {
        // Use custom parser function
        parsedValue = registerInfo.parser(registerValue);
      } else {
        const offset = registerInfo.offset || 0;
        const multiplier = registerInfo.multiplier !== undefined ? registerInfo.multiplier : 1;
        const decimals = registerInfo.decimals ?? (multiplier < 1 ? 1 : 0);

        if (registerInfo.signed) {
          // Convert to signed integer
          const signedValue = registerValue > 32767 ? registerValue - 65536 : registerValue;
          parsedValue = ((signedValue - offset) * multiplier).toFixed(decimals);
        } else {
          // Unsigned value
          parsedValue = ((registerValue - offset) * multiplier).toFixed(decimals);
        }
      }

      parsedData.push({
        address: registerAddress,
        addressHex: '0x' + registerAddress.toString(16).toUpperCase().padStart(4, '0'),
        name: registerInfo.name,
        rawValue: registerValue,
        parsedValue: parsedValue,
        unit: registerInfo.unit,
        description: Array.isArray(parsedValue) ? parsedValue.join(', ') : parsedValue
      });
    });
  });

  return parsedData;
};

